import React from 'react';
import clsx from 'clsx';
import moment from 'moment';
import { Grid, Box, Typography, Toolbar, AppBar, TextField, IconButton, Avatar, CircularProgress, Paper , makeStyles, } from '@material-ui/core';
import {
  fetchChatRoom, fetchMoreFromChatRoom, sendMessage, markMessageRead, isChatBotRoomId, startedTyping, getUserPageURL
} from 'services/chat';
import { useHistory } from 'react-router-dom';
import SendIcon from '@material-ui/icons/Send';
import CloseIcon from '@material-ui/icons/Close';
import InfoIcon from '@material-ui/icons/Info';
import DynamicIcon from 'components/Common/DynamicIcon';
import { DynamicView } from 'components/DynamicScreen';
import Linkify from 'linkifyjs/react';

export const useStyles = makeStyles(theme => ({
  paper: {
    color: theme.palette.text.secondary,
  },
  scrollable: {
    flex: '1 1 auto',
    padding: theme.spacing(2),
    overflowX: 'hidden',
    overflowY: 'auto',
    height: `calc(100vh - ${64+64+64}px)`,
    marginBottom: 64,
  },
  messageBox: {
    whiteSpace: 'pre-line',
  },
  smallAvatar: {
    width: theme.spacing(3),
    height: theme.spacing(3),
  },
  titlebar: {
    background: theme.palette.background.paper,
    color: theme.palette.text.secondary,
  },
  sent: {
    color: '#3f3e60',
  },
  delivered: {
    color: '#3f3e60',
  },
  read: {
    color: '#6890be',
  },
  sentBySelf: {
    backgroundColor:  '#e1ffc7',
  },
  headerNotification: {
    lineHeight: 1, 
    position: 'absolute', 
    bottom: theme.spacing(1),
  },
  selectedUser: {
    backgroundColor: theme.palette.action.selected,
  }
}));

function ChatRoomDetails({ chatRoom, user }) {
  const classes = useStyles();
  const history = useHistory();
  const gotoUser = userId => {
    userId && history.push(getUserPageURL(userId));
  };
  if (chatRoom.room_type === 'single') {
    const otherUser = chatRoom.participants.find(otherUser => otherUser.email !== user.userId);
    otherUser && gotoUser(otherUser.email);
  }
  return <div className={classes.scrollable}>
    <Box p={1}><Typography variant="h6">Participants</Typography></Box>
    <Grid container spacing={1}>
      {chatRoom.participants.map(user => <UserCard 
        key={user.id} 
        avatar={user.avatar_url} 
        name={user.username} 
        onClick={() => gotoUser(user.email)} 
      />)}
    </Grid>
  </div>;
}

function MessageList({ messages, roomId, user }) {
  const classes = useStyles();
  const [hasMore, setHasMore] = React.useState(false);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [allMessages, setAllMessages] = React.useState();
  const messagesRef = React.useRef();
  const LIMIT = 20;

  React.useEffect(() => {
    messages && setAllMessages([...messages]);
    setHasMore(messages && messages.length === LIMIT);
    setTimeout(() => {
      messagesRef && (messagesRef.current.scrollTop = messagesRef.current.scrollHeight);
    }, 100);
  }, [messages, roomId]);

  const onScroll = () => {
    if (messagesRef && messagesRef.current.scrollTop === 0 && hasMore) {
      loadMore();
    };
  };

  const loadMore = () => {
    const lastCommentId = allMessages && allMessages[0] && allMessages[0].id;
    if (!loadingMore && lastCommentId) {
      setLoadingMore(true);
      const oldScrollHeight = messagesRef.current.scrollHeight;
      fetchMoreFromChatRoom(roomId, lastCommentId)
        .then(moreMessages => {
          setAllMessages(allMessages => ([...moreMessages, ...allMessages]));
          setHasMore(moreMessages && moreMessages.length === LIMIT);
          setLoadingMore(false);
          setTimeout(() => {
            messagesRef.current.scrollTop = messagesRef.current.scrollHeight - oldScrollHeight;
          }, 100);
        });
    };
  };

  return <div className={classes.scrollable} ref={messagesRef} onScroll={onScroll}>
    <Box p={1}>
      {hasMore && <Box display='flex' justifyContent='center' p={1} onClick={loadMore}><CircularProgress size={25} /></Box>}
      {allMessages && allMessages.map((message, index) => <MessageCard key={message.id} {...message} user={user} />)}
    </Box>
  </div>;
}

export function UserCard({icon, avatar, name, selected, onClick, ...props}) {
  const classes = useStyles();
  return <Grid item xs={12}>
      <Box p={1} onClick={onClick} className={selected && classes.selectedUser}>
        <Grid container alignItems="center">
          <Grid item xs={2}>
            <Box display="flex" alignItems="center" justifyContent="center">
              {!icon && <Avatar src={avatar} />}
              {icon && <DynamicIcon icon={icon} />}
            </Box>
          </Grid>
          <Grid item xs={10}>
            <Typography variant="h6" noWrap>{name}</Typography>
          </Grid>
        </Grid>      
      </Box>
  </Grid>;
}

export function MessageCard({user, id, date, unix_timestamp, message, payload, status, ...props}) {
  const history = useHistory();
  const classes = useStyles();
  const time_epoch = moment.unix(unix_timestamp);
  const today = moment({hour: 0});
  const senderId = props.username_real || props.email;
  const senderName = props.username_as || props.username;
  const senderAvatar = props.avatar || props.user_avatar_url;
  const sentBySelf = senderId === user.userId;
  const corrected = ({section, ...content}) => ({sections: section, ...content}); // TODO: hack, to be fixed on backend
  const statusIcons = {
    sent: <DynamicIcon icon="Done" className={classes.sent} />,
    delivered: <DynamicIcon icon="DoneAll" className={classes.delivered} />,
    read: <DynamicIcon icon="DoneAll" className={classes.read} />
  };

  return <Grid item xs={12} style={{margin: 8}}>
    <Paper className={clsx(classes.paper, sentBySelf && classes.sentBySelf)}> 
      <Box p={1}>
        <Grid container alignItems="center">
          <Grid item xs={10}>
            <Box px={2} display="flex" flexGrow={1}>
              <Avatar className={classes.smallAvatar} src={senderAvatar}/>
              <Typography variant="subtitle1" noWrap style={{marginLeft: 8}}>{senderName}</Typography>
            </Box>
          </Grid>
          <Grid item xs={2}>
            <Box textAlign="right"> <Typography variant="subtitle2"> 
              {time_epoch.format(time_epoch.isBefore(today) ? 'DD/MM/YYYY HH:mm': 'HH:mm')}
            </Typography> </Box>
          </Grid>
          <Grid item xs={sentBySelf ? 11 : 12}>
            {message && <Typography variant="body1" className={classes.messageBox}>
              <Linkify>{message}</Linkify>
            </Typography>}
            {payload && payload.content && <DynamicView 
              hideToolbar scrollable={false} 
              json={corrected(payload.content)} 
              navigate={({url}) => history.push(url)}
            />}
          </Grid>
          {sentBySelf && <Grid item xs={1}>
            <Box textAlign="right"> {statusIcons[status]}</Box>
          </Grid>}
        </Grid>        
      </Box>
    </Paper>
  </Grid>;
}

export function ChatRoom({ roomId, commandObject, newMessage, onMessageRead, typingEvent, user }) {
  const classes = useStyles();
  const [chatRoom, setChatRoom] = React.useState();
  const [messages, setMessages] = React.useState();
  const [message, setMessage] = React.useState('');
  const [notification, setNotification] = React.useState('');
  const [showDetails, setShowDetails] = React.useState(false);

  React.useEffect(() => {
    setMessage('');
    if (!roomId) {
      setChatRoom(null);
      setMessages(null);
    }
    if (roomId) {
      fetchChatRoom(roomId)
        .then(chatRoom => {
          setShowDetails(false);
          setChatRoom(chatRoom);
          setMessages(chatRoom.comments);
          const lastReceivedMessage = chatRoom.comments.slice(0).reverse()
            .find(comment => comment.username_real !== user.userId && comment.status !== 'read');
          if (lastReceivedMessage) {
            markMessageRead(roomId, lastReceivedMessage.id);
            onMessageRead(lastReceivedMessage);
          }
        });
    }
  }, [roomId, newMessage, onMessageRead, user.userId]);
  // TODO: optimize fetching entire room on new message
  React.useEffect(() => {
    if(commandObject && !commandObject.done) {
      const msg = commandObject.params && commandObject.params[0];
      commandObject.command === 'auto' && setMessage(msg);
      commandObject.command === 'send' && chatRoom && sendMessage(chatRoom.id, msg);
      commandObject.done = true;
    };
  }, [chatRoom, commandObject]);
  React.useEffect(() => {
    if (typingEvent && typingEvent.room_id === roomId) {
      const user = (chatRoom && chatRoom.participants.find(user => user.email === typingEvent.username)) || { username: 'Someone' };
      const notification = chatRoom.room_type === 'single' ? 'is typing ...' : `${user.username} is typing ...`;
      setNotification(typingEvent.message === '1' ? notification : '');
    }
  }, [chatRoom, typingEvent, roomId]);

  const sendMessageToRoom = () => {
    const msg = message && message.trim();
    if (msg) {
      sendMessage(chatRoom.id, msg).then(response => setMessage(''));
    }
  };

  const onKeyPress = e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      sendMessageToRoom();
      return;
    }
    startedTyping();
  };

  return <>
    <AppBar position="relative" elevation={0} className={classes.titlebar}>
      <Toolbar>
        <Box flexGrow={1}>
          <Typography variant="h6" noWrap={true}>{chatRoom && chatRoom.name}</Typography>
          <Typography variant="body2" noWrap={true} className={classes.headerNotification}>{notification}</Typography>
        </Box>
        {chatRoom && !isChatBotRoomId(roomId) &&
          <IconButton tooltipTitle="Details" onClick={() => setShowDetails(!showDetails)}>
            {!showDetails ? <InfoIcon /> : <CloseIcon />}
          </IconButton>}
      </Toolbar>
    </AppBar>
    {chatRoom && <>
      {showDetails && <ChatRoomDetails chatRoom={chatRoom} user={user} />}
      {!showDetails && <>
        <MessageList messages={messages} roomId={roomId} user={user} />
        <Box position="relative">
          <Box display="flex" p={1} position="absolute" width={1} bottom={0}>
            <TextField placeholder='Type Message' fullWidth multiline flex={1} value={message} rowsMax={3}
              onChange={e => setMessage(e.target.value)}
              onKeyPress={onKeyPress} />
            <IconButton onClick={sendMessageToRoom}><SendIcon /></IconButton>
          </Box>
        </Box>
      </>}
    </>}
  </>;
}
